import { useEffect, useState } from 'react';
import { BRAND } from '../brand';
import type { Protokoll } from '../domain/types';
import { DownloadIcon, GeraetSpeicherIcon, HakenIcon, PfeilLinksIcon, TeilenIcon, WarnungIcon } from '../icons';
import { erstelleProtokollPdf, ladePdfHerunter } from '../pdf';
import { ladePdfLokal, pdfQuellHash, speicherePdfLokal } from '../pdfSpeicher';
import { SPRACHEN, UEBERSETZUNGEN, type SpracheCode } from '../uebersetzungen';
import { pruefeVollstaendigkeit } from '../vollstaendigkeit';
import { Markenlogo } from './Marke';

interface AbschlussProps {
	protokoll: Protokoll;
	onAendern: (protokoll: Protokoll) => void;
	onZurueck: () => void;
	onZurueckZurUebersicht: () => void;
}

function fotoZu(protokoll: Protokoll, fotoId: string) {
	return protokoll.fotos.find((f) => f.id === fotoId);
}

interface DokumentFotosProps {
	protokoll: Protokoll;
	fotoIds: string[];
	fotoLabel: string;
}

function DokumentFotos({ protokoll, fotoIds, fotoLabel }: DokumentFotosProps) {
	return (
		<div className="dokument-fotos">
			{fotoIds.map((id, index) => {
				const foto = fotoZu(protokoll, id);
				if (!foto) return null;
				return (
					<figure key={id} className="dokument-foto">
						<img src={foto.datenUrl} alt="" />
						<figcaption>
							{fotoLabel} {index + 1} ·{' '}
							{new Date(foto.aufgenommenAm).toLocaleString('de-DE', {
								dateStyle: 'short',
								timeStyle: 'short'
							})}
						</figcaption>
					</figure>
				);
			})}
		</div>
	);
}

/** Vorschau, Abschluss und vollständig lokaler PDF-Export des Protokolls. */
export function Abschluss({ protokoll, onAendern, onZurueck, onZurueckZurUebersicht }: AbschlussProps) {
	const [sprache, setSprache] = useState<SpracheCode>('de');
	const [bestaetigungOffen, setBestaetigungOffen] = useState(false);
	const [pdfDatei, setPdfDatei] = useState<{ blob: Blob; dateiname: string } | null>(null);
	const [pdfStatus, setPdfStatus] = useState<'wartet' | 'wird-erstellt' | 'gespeichert' | 'fehler'>('wartet');
	const [pdfMeldung, setPdfMeldung] = useState('');
	const texte = UEBERSETZUNGEN[sprache];
	const abgeschlossen = Boolean(protokoll.abgeschlossenAm);
	const pruefpunkte = pruefeVollstaendigkeit(protokoll);

	const gesamtMaengel = protokoll.raeume.reduce(
		(summe, r) => summe + r.maengel.filter((m) => m.zustand === 'mangel').length,
		0
	);

	useEffect(() => {
		let verworfen = false;
		if (!abgeschlossen) {
			setPdfDatei(null);
			setPdfStatus('wartet');
			setPdfMeldung('');
			return;
		}

		async function vorbereiten() {
			setPdfStatus('wird-erstellt');
			setPdfMeldung('');
			try {
				const quellHash = pdfQuellHash(protokoll);
				const gespeichert = await ladePdfLokal(protokoll.id, sprache);
				const istAktuell = gespeichert?.quellHash === quellHash;
				const erstellt = istAktuell
					? { blob: gespeichert.blob, dateiname: gespeichert.dateiname }
					: await erstelleProtokollPdf(protokoll, sprache);
				if (!istAktuell) {
					await speicherePdfLokal(protokoll.id, sprache, quellHash, erstellt.dateiname, erstellt.blob);
				}
				if (!verworfen) {
					setPdfDatei(erstellt);
					setPdfStatus('gespeichert');
				}
			} catch (fehler) {
				if (!verworfen) {
					setPdfDatei(null);
					setPdfStatus('fehler');
					setPdfMeldung(fehler instanceof Error ? fehler.message : 'PDF konnte nicht erstellt werden.');
				}
			}
		}

		void vorbereiten();
		return () => {
			verworfen = true;
		};
	}, [abgeschlossen, protokoll, sprache]);

	function drucken() {
		window.print();
	}

	function herunterladen() {
		if (!pdfDatei) return;
		ladePdfHerunter(pdfDatei.blob, pdfDatei.dateiname);
		setPdfMeldung('PDF wurde heruntergeladen.');
	}

	function teilen() {
		if (!pdfDatei) return;
		const datei = new File([pdfDatei.blob], pdfDatei.dateiname, { type: 'application/pdf' });
		const daten: ShareData = {
			title: `${BRAND.name} – Übergabeprotokoll`,
			text: `Übergabeprotokoll für ${protokoll.objekt.strasse || 'die Immobilie'}`,
			files: [datei]
		};
		if (typeof navigator.share !== 'function' || (navigator.canShare && !navigator.canShare(daten))) {
			ladePdfHerunter(pdfDatei.blob, pdfDatei.dateiname);
			setPdfMeldung('Teilen wird auf diesem Gerät nicht unterstützt. Die PDF wurde stattdessen heruntergeladen.');
			return;
		}
		navigator.share(daten).catch((fehler: unknown) => {
			if (fehler instanceof DOMException && fehler.name === 'AbortError') return;
			setPdfMeldung('Teilen war nicht möglich. Du kannst die PDF weiterhin herunterladen.');
		});
	}

	function alsFertigMarkieren(trotzHinweisen = false) {
		if (pruefpunkte.length > 0 && !trotzHinweisen) {
			setBestaetigungOffen(true);
			return;
		}
		onAendern({ ...protokoll, abgeschlossenAm: new Date().toISOString() });
		setBestaetigungOffen(false);
	}

	return (
		<div className="schritt schritt--abschluss">
			<div className="abschluss-werkzeuge nicht-drucken">
				<button type="button" className="nebenknopf" onClick={onZurueck}>
					<PfeilLinksIcon width={18} height={18} />
					Zurück
				</button>
				<div className="abschluss-werkzeuge-rechts">
					<select
						className="sprach-auswahl"
						value={sprache}
						onChange={(e) => setSprache(e.target.value as SpracheCode)}
						aria-label="Sprache des Dokuments"
					>
						{SPRACHEN.map((s) => (
							<option key={s.code} value={s.code}>
								{s.label}
							</option>
						))}
					</select>
					<button type="button" className="nebenknopf" onClick={onZurueckZurUebersicht}>
						Übersicht
					</button>
					<button type="button" className="nebenknopf" onClick={drucken}>
						Drucken
					</button>
				</div>
			</div>

			{!abgeschlossen && (
				<section className={`abschluss-pruefung nicht-drucken ${pruefpunkte.length === 0 ? 'ist-vollstaendig' : ''}`}>
					<div className="abschluss-pruefung-kopf">
						<span className="abschluss-pruefung-icon">
							{pruefpunkte.length === 0 ? <HakenIcon width={20} height={20} /> : <WarnungIcon width={20} height={20} />}
						</span>
						<div>
							<strong>{pruefpunkte.length === 0 ? 'Bereit zum Abschließen' : `${pruefpunkte.length} Hinweise vor dem Abschluss`}</strong>
							<p>
								{pruefpunkte.length === 0
									? 'Alle wichtigen Nachweise sind vorhanden.'
									: 'Bitte kurz prüfen. Sonderfälle dürfen trotzdem abgeschlossen werden.'}
							</p>
						</div>
					</div>
					{pruefpunkte.length > 0 && (
						<ul className="abschluss-pruefung-liste">
							{pruefpunkte.map((punkt) => (
								<li key={punkt.id}>
									<span>{punkt.bereich}</span>
									{punkt.text}
								</li>
							))}
						</ul>
					)}
					{bestaetigungOffen && (
						<div className="abschluss-bestaetigung" role="alert">
							<p>Die Hinweise bleiben im Protokoll unverändert. Möchtest du es trotzdem als fertig markieren?</p>
							<div>
								<button type="button" className="nebenknopf" onClick={() => setBestaetigungOffen(false)}>
									Weiter prüfen
								</button>
								<button type="button" className="hauptknopf hauptknopf--klein" onClick={() => alsFertigMarkieren(true)}>
									Trotzdem fertig
								</button>
							</div>
						</div>
					)}
				</section>
			)}

			<div className="dokument-vorschau-kopf nicht-drucken">
				<div>
					<span>PDF-Vorschau</span>
					<strong>Bitte vor dem Abschluss einmal vollständig prüfen.</strong>
				</div>
				<span>{texte.label}</span>
			</div>

			<article className="protokoll-dokument" dir={texte.rtl ? 'rtl' : 'ltr'}>
				<header className="dokument-kopf">
					<Markenlogo kompakt className="markenlogo--dokument" />
					<h1>{texte.titel(protokoll.art)}</h1>
					<p>
						{protokoll.objekt.strasse}
						{protokoll.objekt.plz || protokoll.objekt.ort ? `, ${protokoll.objekt.plz} ${protokoll.objekt.ort}`.trim() : ''}
					</p>
					<p className="dokument-meta">
						{texte.erstelltAm} {new Date(protokoll.erstelltAm).toLocaleDateString('de-DE')}
						{gesamtMaengel > 0 && ` · ${texte.maengelAnzahl(gesamtMaengel)}`}
					</p>
				</header>

				<section className="dokument-abschnitt">
					<h2 className="dokument-abschnitt-titel">{texte.raeume}</h2>
					{protokoll.raeume.length === 0 && <p className="dokument-ohne-befund">{texte.keineRaeumeErfasst}</p>}
					{protokoll.raeume.map((raum) => (
						<div key={raum.id} className="dokument-raum">
							<h3>{raum.name}</h3>
							{raum.fotoIds.length > 0 && (
								<DokumentFotos protokoll={protokoll} fotoIds={raum.fotoIds} fotoLabel={texte.foto} />
							)}
							{raum.maengel.length === 0 && <p className="dokument-ohne-befund">{texte.keineAuffaelligkeiten}</p>}
							{raum.maengel.map((mangel) => (
								<div key={mangel.id} className="dokument-mangel">
									<div className="dokument-mangel-text">
										<strong>{mangel.gegenstand}</strong> — {mangel.beschreibung}
										<span className={`dokument-zustand dokument-zustand--${mangel.zustand}`}>
											{texte.zustand[mangel.zustand]}
										</span>
									</div>
									{mangel.fotoIds.length > 0 && (
										<DokumentFotos protokoll={protokoll} fotoIds={mangel.fotoIds} fotoLabel={texte.foto} />
									)}
								</div>
							))}
						</div>
					))}
				</section>

				{protokoll.zaehler.length > 0 && (
					<section className="dokument-abschnitt">
						<h2 className="dokument-abschnitt-titel">{texte.zaehlerstaende}</h2>
						<table className="dokument-tabelle">
							<thead>
								<tr>
									<th>{texte.art}</th>
									<th>{texte.nummer}</th>
									<th>{texte.stand}</th>
								</tr>
							</thead>
							<tbody>
								{protokoll.zaehler.map((z) => (
									<tr key={z.id}>
										<td>{texte.zaehlerArt[z.art]}</td>
										<td>{z.nummer || '—'}</td>
										<td>{z.stand || '—'}</td>
									</tr>
								))}
							</tbody>
						</table>
						<div className="dokument-zaehlerfotos">
							{protokoll.zaehler
								.filter((z) => z.fotoIds.length > 0)
								.map((z) => (
									<div key={z.id} className="dokument-zaehlerfoto-gruppe">
										<h3>
											{texte.zaehlerArt[z.art]}
											{z.nummer ? ` · ${z.nummer}` : ''}
										</h3>
										<DokumentFotos protokoll={protokoll} fotoIds={z.fotoIds} fotoLabel={texte.foto} />
									</div>
								))}
						</div>
					</section>
				)}

				{protokoll.schluessel.length > 0 && (
					<section className="dokument-abschnitt">
						<h2 className="dokument-abschnitt-titel">{texte.schluessel}</h2>
						<table className="dokument-tabelle">
							<thead>
								<tr>
									<th>{texte.art}</th>
									<th>{texte.anzahl}</th>
								</tr>
							</thead>
							<tbody>
								{protokoll.schluessel.map((s) => (
									<tr key={s.id}>
										<td>{s.art}</td>
										<td>{s.anzahl}</td>
									</tr>
								))}
							</tbody>
						</table>
					</section>
				)}

				{protokoll.beteiligte.length > 0 && (
					<section className="dokument-abschnitt">
						<h2 className="dokument-abschnitt-titel">{texte.unterschriften}</h2>
						<div className="dokument-unterschriften">
							{protokoll.beteiligte.map((b) => (
								<div key={b.id} className="dokument-unterschrift">
									<div className="dokument-unterschrift-bild">
										{b.unterschrift ? (
											<img src={b.unterschrift} alt={`${b.name}`} />
										) : (
											<span className="dokument-unterschrift-fehlt">{texte.nochNichtUnterschrieben}</span>
										)}
									</div>
									<p className="dokument-unterschrift-name">
										{b.name} <span>· {texte.rolle[b.rolle]}</span>
									</p>
								</div>
							))}
						</div>
					</section>
				)}

				<footer className="dokument-fuss">
					<p>
						{texte.erstelltMit} {BRAND.name}
					</p>
				</footer>
			</article>

			{!abgeschlossen && (
				<div className="abschluss-fertigstellen nicht-drucken">
					<div>
						<span>Letzter Schritt</span>
						<strong>Alles geprüft?</strong>
						<p>Nach dem Abschluss wird die fertige PDF lokal erzeugt. Du kannst sie anschließend teilen oder herunterladen.</p>
					</div>
					<button
						type="button"
						className="hauptknopf hauptknopf--bestaetigen"
						onClick={() => alsFertigMarkieren()}
					>
						{pruefpunkte.length > 0 ? 'Hinweise prüfen und abschließen' : 'Protokoll abschließen'}
					</button>
				</div>
			)}

			{abgeschlossen && (
				<section className="pdf-aktionen nicht-drucken" aria-labelledby="pdf-aktionen-titel">
					<div className="pdf-aktionen-kopf">
						<span className="pdf-aktionen-haken">
							<HakenIcon width={21} height={21} />
						</span>
						<div>
							<span>Übergabe abgeschlossen</span>
							<h2 id="pdf-aktionen-titel">Was möchtest du mit der PDF machen?</h2>
							<p>
								Die Datei bleibt auf diesem Gerät. Für Teilen oder Download ist kein {BRAND.name}-Server nötig.
							</p>
						</div>
					</div>

					<div className="pdf-aktionen-raster">
						<button
							type="button"
							className="pdf-aktion pdf-aktion--primaer"
							onClick={teilen}
							disabled={pdfStatus !== 'gespeichert'}
						>
							<TeilenIcon width={22} height={22} />
							<span>
								<strong>PDF teilen</strong>
								<small>Mail, WhatsApp, AirDrop, Drive …</small>
							</span>
						</button>
						<button
							type="button"
							className="pdf-aktion"
							onClick={herunterladen}
							disabled={pdfStatus !== 'gespeichert'}
						>
							<DownloadIcon width={22} height={22} />
							<span>
								<strong>PDF herunterladen</strong>
								<small>Als Datei auf dem Gerät sichern</small>
							</span>
						</button>
						<div className={`pdf-aktion pdf-aktion--status pdf-aktion--${pdfStatus}`}>
							<GeraetSpeicherIcon width={22} height={22} />
							<span>
								<strong>
									{pdfStatus === 'wird-erstellt' && 'PDF wird erstellt …'}
									{pdfStatus === 'gespeichert' && `In ${BRAND.name} gespeichert`}
									{pdfStatus === 'fehler' && 'PDF noch nicht gespeichert'}
									{pdfStatus === 'wartet' && 'PDF wird vorbereitet'}
								</strong>
								<small>{pdfStatus === 'gespeichert' ? 'Offline auf diesem Gerät verfügbar' : 'Keine Daten werden hochgeladen'}</small>
							</span>
						</div>
					</div>

					{pdfMeldung && (
						<p className={`pdf-meldung ${pdfStatus === 'fehler' ? 'pdf-meldung--fehler' : ''}`} role="status">
							{pdfMeldung}
						</p>
					)}
				</section>
			)}
		</div>
	);
}
